import type { GetRootState } from 'js/src/libs/RootState';

/**
 * Lets overlapping callers of a data-loading operation share the one request that is already
 * running, instead of each starting their own or polling the store until the data shows up.
 *
 * The promise covers the whole operation — the fetch and the dispatches that store its result — so
 * a caller that awaits it can read the loaded data from the store immediately afterwards.
 *
 * Promises can't be kept in redux, so the registry lives in this module. It is partitioned by the
 * `getState` identity that every thunk and luminary operation already receives, which stands in for
 * the store: one registry per store, collected with it, and no leakage between test suites that
 * each build their own.
 */
type Registry = Map<string, Promise<unknown>>;

const registries = new WeakMap<GetRootState, Registry>();

function registryFor(getState: GetRootState): Registry {
    const existing = registries.get(getState);
    if (existing) {
        return existing;
    }
    const registry: Registry = new Map();
    registries.set(getState, registry);
    return registry;
}

function start<T>(registry: Registry, key: string, run: () => Promise<T>): Promise<T> {
    const promise = run().finally(() => {
        // A forced refetch may have replaced this entry while it was still running; only the
        // current occupant gets to clear the key.
        if (registry.get(key) === promise) {
            registry.delete(key);
        }
    });
    registry.set(key, promise);
    return promise;
}

export function joinOrStart<T>(
    getState: GetRootState,
    key: string,
    run: () => Promise<T>,
): Promise<T> {
    const registry = registryFor(getState);
    const existing = registry.get(key) as Promise<T> | undefined;
    if (existing) {
        return existing;
    }
    return start(registry, key, run);
}

export function startFresh<T>(
    getState: GetRootState,
    key: string,
    run: () => Promise<T>,
): Promise<T> {
    return start(registryFor(getState), key, run);
}
